import { PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Empty, Input, Pagination, Skeleton, Tag, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  createVirtualIpProject,
  listVirtualIpAssets,
  type SaasApiError,
  type VirtualIpAssetSummary,
} from '../../api'
import {
  virtualIpAssetDetailPath,
  virtualIpProjectBase,
  virtualIpProjectsPath,
} from './virtualIpPaths'
import './virtualIp.css'
import '../../ui/tenant-items.css'
import '../contentProduction/content-production.css'

const PAGE_SIZE = 16

function formatUpdatedAt(value: string | null | undefined): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

export default function VirtualIpAssetListPage() {
  const { t } = useTranslation('virtualIp')
  const { workspaceId = '' } = useParams()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<VirtualIpAssetSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQ(q.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [q])

  const load = useCallback(async () => {
    if (!workspaceId) return
    setLoading(true)
    try {
      const { data } = await listVirtualIpAssets(workspaceId, {
        q: debouncedQ || undefined,
        page,
        page_size: PAGE_SIZE,
      })
      setItems(data.items)
      setTotal(data.total)
    } catch (e) {
      message.error((e as SaasApiError).message || t('ia.loadAssetsFailed'))
    } finally {
      setLoading(false)
    }
  }, [workspaceId, debouncedQ, page, t])

  useEffect(() => {
    void load()
  }, [load])

  const onCreate = async () => {
    if (!workspaceId) return
    setCreating(true)
    try {
      const { data } = await createVirtualIpProject(workspaceId)
      navigate(`${virtualIpProjectBase(workspaceId, data.id)}/step-1`)
    } catch (e) {
      message.error((e as SaasApiError).message || t('ia.createProjectFailed'))
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="tc-items-page cpg-page" data-testid="virtual-ip-asset-list">
      <div className="tc-page-header">
        <div>
          <h2 className="tc-page-title">{t('ia.assetsTitle')}</h2>
          <p className="tc-page-desc">{t('ia.assetsSubtitle')}</p>
        </div>
        <div className="tc-page-actions vip-ia-asset-count" data-testid="virtual-ip-asset-count">
          {t('ia.assetCount', { count: total })}
        </div>
      </div>

      <div className="tc-data-card">
        <div className="cpg-list-toolbar">
          <div className="cpg-list-toolbar-search">
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={t('ia.searchAssets')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ width: 260 }}
              data-testid="virtual-ip-asset-search"
            />
          </div>
        </div>

        {loading && items.length === 0 ? (
          <div className="cpg-gallery-grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="cpg-card cpg-card--skeleton">
                <Skeleton.Image active style={{ width: '100%', height: 180 }} />
                <Skeleton active paragraph={{ rows: 2 }} />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <Empty description={t('ia.emptyAssets')} image={Empty.PRESENTED_IMAGE_SIMPLE}>
            <Button type="primary" icon={<PlusOutlined />} loading={creating} onClick={() => void onCreate()}>
              {t('ia.newProject')}
            </Button>
            <div style={{ marginTop: 8 }}>
              <Button type="link" onClick={() => navigate(virtualIpProjectsPath(workspaceId))}>
                {t('ia.goToProjects')}
              </Button>
            </div>
          </Empty>
        ) : (
          <>
            <div className="cpg-gallery-grid" data-testid="virtual-ip-asset-grid">
              {items.map((asset) => (
                <article
                  key={asset.id}
                  className="cpg-card vip-ia-card"
                  role="button"
                  tabIndex={0}
                  data-testid={`virtual-ip-asset-card-${asset.id}`}
                  onClick={() => navigate(virtualIpAssetDetailPath(workspaceId, asset.id))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(virtualIpAssetDetailPath(workspaceId, asset.id))
                    }
                  }}
                >
                  <div className="cpg-card-visual vip-ia-card-visual">
                    {asset.cover_url ? (
                      <img src={asset.cover_url} alt="" className="vip-ia-card-cover" />
                    ) : (
                      <div className="vip-ia-card-placeholder">{t('ia.coverPlaceholder')}</div>
                    )}
                  </div>
                  <div className="cpg-card-footer vip-ia-card-footer">
                    <div className="vip-ia-card-meta">
                      <span className="cpg-card-footer-title" title={asset.name}>
                        {asset.name}
                      </span>
                      {asset.one_liner ? <p className="vip-ia-card-oneliner">{asset.one_liner}</p> : null}
                      <div className="vip-ia-card-tags">
                        {asset.tags.slice(0, 3).map((tag) => (
                          <Tag key={tag}>{tag}</Tag>
                        ))}
                      </div>
                      <div className="vip-ia-card-sub">
                        <Tag color="success">{t('ia.projectStatus.completed')}</Tag>
                        <span className="vip-ia-card-updated">{formatUpdatedAt(asset.updated_at)}</span>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            {total > PAGE_SIZE ? (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <Pagination
                  current={page}
                  pageSize={PAGE_SIZE}
                  total={total}
                  onChange={setPage}
                  showSizeChanger={false}
                />
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
